/**
 * Definir os tipos de ações possíveis
 *
 */
export type OperacaoDePermissionamento =
  | "listar"
  | "criar"
  | "visualizar"
  | "finalizar"
  | "editar"
  | "deletar"
  | "iniciar"
  | "arquivar"
  | "desarquivar"
  | "travar"
  | "destravar"
  | "ativar"
  | "desativar"
/**
 * interface para construir uma estrutura de dados que representa a permissão de um ator
 *
 */
interface PermissaoDeAtor {
  operacao: OperacaoDePermissionamento
  intriseca: boolean
  negada: boolean
}
/**
 * @class classe para implementa o permissionamento
 *
 * @param {OperacaoDePermissionamento} operacao - tipo de operação a ser realizada sobre um recurso
 * @param {Boolean} intriseca - para decidir se o ator pode realizar uma operação sobre o seu próprio recurso
 * @param {Boolean} negada - para decidir se o ator tem permissão garantida para realizar a operação
 */
export class Permissao implements PermissaoDeAtor {
  operacao: OperacaoDePermissionamento
  intriseca: boolean
  negada: boolean

  /**
   * Criar uma instancia de Permissao
   *
   * @param {OperacaoDePermissionamento} operacao - tipo de operação a ser realizada sobre um recurso
   * @param {Boolean} intriseca - para decidir se o ator pode realizar uma operação sobre o seu próprio recurso
   * @param {Boolean} negada - para decidir se o ator tem permissão garantida para realizar a operação
   */
  constructor(
    operacao: OperacaoDePermissionamento,
    intriseca = false,
    negada = false
  ) {
    this.operacao = operacao
    this.intriseca = intriseca
    this.negada = negada
  }

  verificarAutoNegacao(resultadoExterno: boolean): boolean {
    if (this.negada) {
      return !resultadoExterno
    }
    return resultadoExterno
  }

  verificarExigenciaDeRelacionamentoIntrinseco(
    resultadoExterno: boolean
  ): boolean {
    return this.intriseca && resultadoExterno
  }
}

/**
 * Negar a permissão para realizar uma dada operação
 */
const NaoPermiteDeletarProprios = new Permissao("deletar", true, true)
const NaoPermiteDesativarProprios = new Permissao("desativar", true, true)

/**
 * garantir todas as permissões possíveis
 *
 * */
const PermiteListar = new Permissao("listar")
const PermiteVisualizar = new Permissao("visualizar")
const PermiteCriar = new Permissao("criar")
const PermiteIniciar = new Permissao("iniciar")
const PermiteFinalizar = new Permissao("finalizar")
const PermiteEditar = new Permissao("editar")
const PermiteDeletar = new Permissao("deletar")
const PermiteAtivar = new Permissao("ativar")
const PermiteDesativar = new Permissao("desativar")
const PermiteArquivar = new Permissao("arquivar")
const PermiteDesarquivar = new Permissao("desarquivar")
const PermiteTravar = new Permissao("travar")
const PermiteDestravar = new Permissao("destravar")

/**
 * Garantir permissões sobre recursos próprios do ator
 *
 * */
const PermiteVisualizarProprios = new Permissao("visualizar", true)
const PermiteListarProprios = new Permissao("listar", true)
const PermiteIniciarProprios = new Permissao("iniciar", true)
const PermiteFinalizarProprios = new Permissao("finalizar", true)
const PermiteEditarProprios = new Permissao("editar", true)
const PermiteDeletarProprios = new Permissao("deletar", true)

/**
 * Interface para servir de modelo para criação de Módulos de permissão no frontend
 */
export type GrupoDePermissoes = {
  administrador: Permissao[]
  /**
   * Papel de pessoal, é mais básico
   */
  pessoal: Permissao[]
  /**
   * Papel de supervisor
   */
  supervisor: Permissao[]
  gerente: Permissao[]
  /**
   * Papel de visualizador
   */
  visualizador: Permissao[]
}


/**
 * Módulo de permissões para o recurso Usuarios
 *
 */
const Usuarios: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer todas as ações sobre o recurso
   * Usuarios exceto deletar seu próprio usuário
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteAtivar,
    PermiteDesativar,
    NaoPermiteDeletarProprios,
    NaoPermiteDesativarProprios
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode listar os usuários
   */
  pessoal: [PermiteListar],
  /**
   *
   * Com permissão de supervisor, o ator somente pode listar os usuários
   */
  supervisor: [PermiteListar],
  gerente: [PermiteListar],

  /**
   *
   * Com permissão de visualizador, o ator somente pode listar os usuários
   */
  visualizador: [PermiteListar, PermiteVisualizar],
}

/**
 * Determina o Módulo de permissões para o recurso Projetos de acordo com o papel do ator
 *
 */
const Projetos: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Projetos
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteArquivar,
    PermiteDesarquivar,
  ],
  /**
   *
   * Com permissão de pessoal, o ator pode somente listar os Projetos
   */
  pessoal: [PermiteListar],
  /**
   *
   * Com permissão de supervisor, o ator pode somente criar, visualizar e listar Projetos
   */
  supervisor: [
    PermiteCriar,
    PermiteVisualizar,
    PermiteListar,
    PermiteEditarProprios,
  ],
  gerente: [
    PermiteCriar,
    PermiteVisualizar,
    PermiteListar,
    PermiteEditarProprios,
  ],

  // supervisor: [PermiteVisualizar, PermiteListar],
  /**
   *
   * Com permissão de visualizador, o ator pode somente listar os Projetos
   */
  visualizador: [PermiteListar, PermiteVisualizar],
}

/**
 * Determina o Módulo de permissões para o recurso Recursos de acordo com o papel do ator
 *
 */
const Recursos: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Recursos
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteDesarquivar,
    PermiteArquivar
  ],
  /**
   *
   * Com permissão de pessoal, o ator pode somente listar os Recursos
   */
  pessoal: [PermiteListar],
  /**
   *
   * Com permissão de supervisor, o ator pode somente criar e visualizar Recursos
   */
  supervisor: [PermiteCriar, PermiteVisualizar],
  gerente: [PermiteCriar, PermiteVisualizar],

  /**
   *
   * Com permissão de visualizador, o ator pode somente listar os Recursos
   */
  visualizador: [PermiteListar, PermiteVisualizar],
}

/**
 * Determina o Módulo de permissões para o recurso Tipos de Recurso de acordo com o papel do ator
 *
 */
const TiposDeRecursos: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Tipos de Recurso
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteArquivar,
    PermiteDesarquivar
  ],
  /**
   *
   * Com permissão de pessoal, o ator não pode fazer nada sobre o recurso Tipos de Recurso
   */
  pessoal: [],
  /**
   *
   * Com permissão de supervisor, o ator somente pode listar os Tipos de Recurso
   */
  supervisor: [PermiteListar],
  gerente: [PermiteListar],

  /**
   *
   * Com permissão de visualizador, o ator não pode fazer nada sobre o recurso Tipos de Recurso
   */
  visualizador: [],
}

/**
 * Determina o Módulo de permissões para o recurso Profissionais de acordo com o papel do ator
 *
 */
const Profissionais: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Profissionais
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteArquivar,
    PermiteDesarquivar,
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode visualizar o seu próprio Profissional
   */
  pessoal: [PermiteVisualizarProprios],
  /**
   *
   * Com permissão de supervisor, o ator somente pode listar, criar e visualizar os Profissionais
   */
  supervisor: [PermiteListar, PermiteCriar, PermiteVisualizar],
  gerente: [PermiteListar, PermiteCriar, PermiteVisualizar],

  /**
   *
   * Com permissão de visualizador, o ator somente pode visualizar o seu próprio Profissional
   */
  visualizador: [PermiteVisualizar],
}

/**
 * Determina o Módulo de permissões para o recurso Categoria de profissionais de acordo com o papel do ator
 *
 */
const CategoriasDeProfissionais: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Categorias
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteArquivar,
    PermiteDesarquivar
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode listar suas próprias Categorias
   */
  pessoal: [PermiteListarProprios],
  /**
   *
   * Com permissão de supervisor, o ator somente pode listar as Categorias
   */
  supervisor: [PermiteListar],
  gerente: [PermiteListar],

  /**
   *
   * Com permissão de visualizador, o ator somente pode listar suas próprias Categorias
   */
  visualizador: [PermiteListarProprios],
}

/**
 * Determina o Módulo de permissões para o recurso Atributos de acordo com o papel do ator
 *
 */
const Atributos: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Atributos
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode listar os seus próprios Atributos
   */
  pessoal: [PermiteListarProprios],
  /**
   *
   * Com permissão de supervisor, o ator somente pode listar todos os Atributos
   */
  supervisor: [PermiteListar],
  gerente: [PermiteListar],

  /**
   *
   * Com permissão de visualizador, o ator somente pode listar os seus próprios Atributos
   */
  visualizador: [PermiteListarProprios],
}

/**
 * Determina o Módulo de permissões para o recurso Tipos de Atividade de acordo com o papel do ator
 *
 */
const TiposDeAtividade: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Tipos de Atividade
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
    PermiteArquivar,
    PermiteDesarquivar
  ],
  /**
   *
   * Com permissão de pessoal, o ator não pode fazer nada sobre o recurso Tipos de Atividade
   */
  pessoal: [],
  /**
   *
   * Com permissão de supervisor, o ator somente pode criar, visualizar, listar e editar todos os Tipos de Atividade,
   * e somente pode deletar os seus próprios Tipos de Atividade, ou seja, os tipos de Atividade que ele criou.
   */
  supervisor: [
    PermiteCriar,
    PermiteVisualizar,
    PermiteListar,
    PermiteEditar,
    PermiteDeletarProprios,
  ],
  gerente: [
    PermiteCriar,
    PermiteVisualizar,
    PermiteListar,
    PermiteEditar,
    PermiteDeletarProprios,
  ],

  /**
   *
   * Com permissão de visualizador, o ator somente pode visualizar todos os Tipos de Atividade
   */
  visualizador: [PermiteVisualizar],
}

/**
 * Determina o Módulo de permissões para o recurso Atividades Realizadas de acordo com o papel do ator
 *
 */
const AtividadesRealizadas: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Atividades Realizadas
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciarProprios,
    PermiteFinalizarProprios,
    PermiteEditar,
    PermiteDeletar,
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode visualizar, criar e iniciar quaisquer Atividades Realizadas.
   * Somente pode editar, finalizar e deletar suas próprias Atividades Realizadas, ou seja, as que ele criou.
   */
  pessoal: [
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciarProprios,
    PermiteFinalizarProprios,
    PermiteEditarProprios,
    PermiteDeletarProprios,
    PermiteFinalizarProprios,
  ],
  /**
   *
   * Com permissão de supervisor, o ator somente pode visualizar, criar e iniciar quaisquer Atividades Realizadas.
   * Somente pode editar, finalizar e deletar suas próprias Atividades Realizadas, ou seja, as que ele criou.
   */
  supervisor: [
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciarProprios,
    PermiteFinalizarProprios,
    PermiteEditarProprios,
    PermiteDeletarProprios,
  ],
  gerente: [
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciarProprios,
    PermiteFinalizarProprios,
    PermiteEditarProprios,
    PermiteDeletarProprios,
  ],

  /**
   *
   * Com permissão de visualizador, o ator somente pode visualizar quaisquer Atividades Realizadas.
   */
  visualizador: [
    PermiteVisualizar,
  ]
}

/**
 * Determina o Módulo de permissões para o recurso Atividades Realizadas de acordo com o papel do ator
 *
 */
const Eventos: GrupoDePermissoes = {
  /**
   *
   * Com permissão de administrador, o ator pode fazer quaisquer ações sobre o recurso Atividades Realizadas
   */
  administrador: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
  ],
  /**
   *
   * Com permissão de pessoal, o ator somente pode visualizar, criar e iniciar quaisquer Atividades Realizadas.
   * Somente pode editar, finalizar e deletar suas próprias Atividades Realizadas, ou seja, as que ele criou.
   */
  pessoal: [],
  /**
   *
   * Com permissão de supervisor, o ator somente pode visualizar, criar e iniciar quaisquer Atividades Realizadas.
   * Somente pode editar, finalizar e deletar suas próprias Atividades Realizadas, ou seja, as que ele criou.
   */
  supervisor: [
    PermiteListar,
    PermiteVisualizar,
    PermiteCriar,
    PermiteIniciar,
    PermiteFinalizar,
    PermiteEditar,
    PermiteDeletar,
  ],
  gerente: [],

  /**
   *
   * Com permissão de visualizador, o ator não pode fazer nenhuma ação relacionada aos Eventos
   */
  visualizador: [],
}

/**
 * Determina o Módulo de permissões para o recurso Travamento de acordo com o papel do ator
 *
 */
const Travamento: GrupoDePermissoes = {
  administrador: [PermiteVisualizar, PermiteDestravar, PermiteTravar],
  pessoal: [],
  supervisor: [PermiteTravar],
  gerente: [],

  visualizador: [],
}

const Importacoes: GrupoDePermissoes = {
  administrador: [PermiteVisualizar],
  pessoal: [],
  supervisor: [],
  gerente: [],

  visualizador: [],
}

export const modulosDePermissao = {
  Usuarios,
  Projetos,
  Recursos,
  TiposDeRecursos,
  Profissionais,
  CategoriasDeProfissionais,
  Atributos,
  TiposDeAtividade,
  AtividadesRealizadas,
  Eventos,
  Travamento,
  Importacoes,
}
